import { useApp } from '@/App';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import formRule from '@/libs/antd/rules';
import {
	Pipeline,
	ProspectSteps,
	getPipelineDetail,
	updatePipelineCCDH,
} from '@/libs/fleet-api/services/pipeline';
import { LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Button,
	Col,
	Divider,
	Flex,
	Form,
	notification,
	Result,
	Row,
	Table,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { FC, Fragment, useState } from 'react';
import PipelineMainInformation from '../../../components/pipeline/PipelineMainInformation';
import fetcher from '@/libs/fleet-api/fetcher';
import DataRow from '@/libs/antd/components/DataRow';
import { formatCurrency } from '@/libs/antd/formater';
import GenericErrorMessage from '@/components/GenericErrorMessage';

type CCDHPipelinePageProps = {
	pipelineId: number;
};

const CCDHPipelinePage: FC<CCDHPipelinePageProps> = ({ pipelineId }) => {
	const { navigate } = useApp();

	const [successSaveModal, setSuccessSaveModal] = useState<boolean>(false);

	const { form, fieldName } = useForm<Pipeline<'payload'>>();

	const saveCCDHPipelineMutation = useMutation({
		mutationFn: updatePipelineCCDH,
	});

	const { data, isLoading, refetch, isError } = useQuery({
		queryKey: ['getPipelineDetail', pipelineId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getPipelineDetail(id);
		},
		enabled: !!pipelineId,
	});

	if (isError) return <GenericErrorMessage />;

	if (isLoading) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	if (!data) return null;

	if (!data.data.pipelineDetail) return <h2>No Pipeline Data</h2>;

	const {
		pipelineDetail,
		customerDocuments,
		prospectInformation,
		pipelineApprovalHistory: approvalHistory,
	} = data.data;

	const selectedPlafons =
		prospectInformation?.financingApplicationLimitInformation.filter(
			(plafon) => {
				if (plafon.plafonType) {
					return pipelineDetail.pipelinePlafonTypeIds.includes(
						plafon.plafonType.id,
					);
				}
				return false;
			},
		);

	const onSubmit = async (values: Pipeline<'payload'>) => {
		try {
			await saveCCDHPipelineMutation.mutateAsync({
				...values,
				prospectId: pipelineDetail.prospectId,
				id: pipelineDetail.pipelineHotProspectId,
			});
			refetch();
			setSuccessSaveModal(true);
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages?.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li key={msg}>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	return (
		<Fragment>
			<FleetModal
				open={successSaveModal}
				okText="NEXT"
				onOk={() => {
					setSuccessSaveModal(false);

					return navigate('CCDHPipelineListPage');
				}}
				onCancel={() => {
					setSuccessSaveModal(false);

					return navigate('CCDHPipelineListPage');
				}}
				cancelButtonProps={{ style: { display: 'none' } }}
			>
				<Result
					status="success"
					title={<div style={{ fontSize: 30 }}>Sukses Menyetujui</div>}
				/>
			</FleetModal>
			<PipelineMainInformation
				mainInformation={{
					prospectId: pipelineDetail.prospectId,
					napNo: pipelineDetail.napNo,
					prospectStatus: pipelineDetail.prospectStatus.value ?? '',
					orderSource: pipelineDetail.orderSource?.value ?? '',
					customerId: pipelineDetail.customerId ?? '',
					customerName: pipelineDetail.customerName ?? '',
					customerGroup: pipelineDetail.customerGroup ?? { id: 0, value: '' },
					industrySector: pipelineDetail.industrySector ?? { id: 0, value: '' },
					slikResult: '',
					frm: { id: 0, value: '' },
					creditAnalyst: { id: 0, value: '' },
					existingPlafond: pipelineDetail.existingFinancingLimit ?? 0,
					appliedPlafond: pipelineDetail.limitApplication ?? 0,
					applicationStatus: pipelineDetail.prospectStatus.value ?? '',
					newOrderDate: pipelineDetail.newOrderDate ?? '',
					outstandingPrincipal: pipelineDetail?.outstandingPrincipal ?? 0,
				}}
				customerBusinessDocuments={customerDocuments.customerBusinessDocuments}
				customerLegalDocuments={customerDocuments.customerLegalDocuments}
				customerReferralDocuments={customerDocuments.customerReferralDocuments}
				isLoading={isLoading}
				prospectStatusId={
					(pipelineDetail.prospectStatus.id ??
						ProspectSteps.Bch) as unknown as ProspectSteps
				}
			/>
			{selectedPlafons.length > 0 &&
				selectedPlafons.map((plafon) => (
					<div key={plafon.prospectPlafonId}>
						<FleetTitle level={3}>Plafon {plafon.plafonType?.value}</FleetTitle>
						<DataRow
							style={{ marginBottom: 20 }}
							items={[
								[
									[
										'Outstanding Principal',
										formatCurrency(plafon.outstandingPrincipal),
									],
									[
										'Plafond Existing',
										formatCurrency(plafon.existingFinancingLimit),
									],
								],
								[
									['Plafon Diajukan', formatCurrency(plafon?.limitApplication)],
									['Detail Sumber Order', plafon.referrer?.unitName],
								],
							]}
						/>
					</div>
				))}
			<Divider />
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				name="productOffer"
				onFinish={(values) => onSubmit(values)}
				initialValues={{
					creditReview: pipelineDetail.creditReview,
					notes: pipelineDetail.approvalNotes,
				}}
			>
				<Row gutter={[20, 40]}>
					<Col
						span={5}
						style={{ fontWeight: 600, fontSize: 14 }}
					>
						Ultimate Owner
					</Col>
					<Col
						span={19}
						style={{ marginLeft: -10 }}
					>
						{pipelineDetail.ultimateOwner}
					</Col>
				</Row>

				<Divider />

				<Row gutter={[20, 40]}>
					<Col
						span={5}
						style={{ fontWeight: 600, fontSize: 14 }}
					>
						Current Issue
					</Col>
					<Col
						span={19}
						style={{ marginLeft: -10 }}
					>
						{pipelineDetail.currentIssue}
					</Col>
				</Row>

				<Divider />

				<Row gutter={[20, 40]}>
					<Col
						span={5}
						style={{ fontWeight: 600, fontSize: 14 }}
					>
						Skema Kredit
					</Col>
					<Col
						span={19}
						style={{ marginLeft: -10 }}
					>
						{pipelineDetail.creditScheme}
					</Col>
				</Row>

				<Divider />

				<Row gutter={[20, 40]}>
					<Col span={24}>
						<Form.Item
							name={fieldName('creditReview')}
							label="Review Kredit"
							labelCol={{ span: 5 }}
							style={{
								marginLeft: -5,
							}}
							rules={[formRule.required()]}
						>
							<TextArea rows={3} />
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={[20, 40]}>
					<Col span={12}>
						<Row>
							<Col
								span={10}
								style={{ fontWeight: 600, fontSize: 14 }}
							>
								Rekomendasi Kredit CCA
							</Col>
							<Col span={12}>{pipelineDetail.creditRecommendation.value}</Col>
						</Row>
					</Col>
					<Col span={12}>
						<Row>
							<Col
								span={10}
								style={{ fontWeight: 600, fontSize: 14 }}
							>
								Notes Rekomendasi CCA
							</Col>
							<Col
								span={12}
								style={{ marginLeft: -10 }}
							>
								{pipelineDetail.creditNotes}
							</Col>
						</Row>
					</Col>
				</Row>

				<Divider />

				<FleetTitle>Approval History</FleetTitle>
				<Table
					bordered
					loading={isLoading}
					pagination={false}
					dataSource={
						approvalHistory?.map((approval, key) => {
							return {
								key,
								...approval,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'No',
							render: (_, record) => record.no,
						},
						{
							title: 'Name',
							render: (_, record) => record.approvalBy,
						},
						{
							title: 'Date',
							render: (_, record) => record.approvalDate,
						},
						{
							title: 'Process',
							render: (_, record) => record.approvalProcess,
						},
						{
							title: 'Status',
							align: 'center',
							render: (_, record) => {
								return (
									<span
										className={`review-status ${record.approvalStatus.toLowerCase()}`}
									>
										{record.approvalStatus}
									</span>
								);
							},
						},
						{
							title: 'Approval Notes',
							render: (_, record) => record.approvalNote,
						},
					]}
					style={{ marginBottom: 20 }}
				/>
				<Divider />

				<Row gutter={[20, 40]}>
					<Col span={24}>
						<Form.Item
							name={fieldName('approvalNotes')}
							label="Notes"
							labelCol={{ span: 5 }}
							style={{
								marginLeft: -5,
							}}
							rules={[formRule.required()]}
						>
							<TextArea rows={3} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Flex
					justify="flex-end"
					gap={12}
				>
					<Button
						type="primary"
						htmlType="submit"
						loading={saveCCDHPipelineMutation.isPending}
						style={{
							width: 200,
							display: 'block',
							marginTop: 30,
						}}
					>
						SAVE
					</Button>
				</Flex>
			</Form>
			<Divider />
		</Fragment>
	);
};

export default CCDHPipelinePage;
